// frontend/src/components/crm/EmailTemplateBuilder.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import crmApi, { emailTemplateAPI } from '../../utils/crmApi';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useDialog } from '../../components/kibo-ui/dialog';
import {
    ArrowLeftIcon,
    DocumentTextIcon,
    CodeBracketIcon,
    EyeIcon,
    SparklesIcon,
    ArrowPathIcon,
    CheckCircleIcon,
    XCircleIcon,
    PlusIcon,
    TrashIcon,
    TagIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

// Rich Text Editor Component (same as in EmailTemplatesEnhanced)
const RichTextEditor = ({ value, onChange, height = 300, placeholder = "Enter your content..." }) => {
    const [isFocused, setIsFocused] = useState(false);
    const [showVariables, setShowVariables] = useState(false);
    
    const availableVariables = [
        { name: 'client.name', description: 'Client full name', example: 'John Doe' },
        { name: 'client.email', description: 'Client email address', example: 'john@example.com' },
        { name: 'client.company', description: 'Client company name', example: 'Acme Corp' },
        { name: 'account.name', description: 'Account company name', example: 'Tech Solutions Inc' },
        { name: 'account.contact', description: 'Account contact person', example: 'Jane Smith' },
        { name: 'partner.name', description: 'Assigned partner name', example: 'Maria Garcia' },
        { name: 'current.date', description: 'Current date', example: '2024-01-15' },
        { name: 'current.time', description: 'Current time', example: '14:30' },
        { name: 'task.title', description: 'Task title', example: 'NIE Application' },
        { name: 'task.dueDate', description: 'Task due date', example: '2024-02-01' },
        { name: 'agency.name', description: 'Agency name', example: 'LiveLoveSpain' },
        { name: 'agency.motto', description: 'Agency motto', example: 'Your Spanish Journey Starts Here' }
    ];

    const insertVariable = (variableName) => {
        const variableText = `{{${variableName}}}`;
        const textarea = document.getElementById('rich-text-editor');
        if (textarea) {
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const newValue = value.substring(0, start) + variableText + value.substring(end);
            onChange(newValue);
            
            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(start + variableText.length, start + variableText.length);
            }, 0);
        }
    };

    const formatText = (command) => {
        const textarea = document.getElementById('rich-text-editor');
        if (textarea) {
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const selectedText = value.substring(start, end);
            
            let formattedText = selectedText;
            switch (command) {
                case 'bold':
                    formattedText = `<strong>${selectedText}</strong>`;
                    break;
                case 'italic':
                    formattedText = `<em>${selectedText}</em>`;
                    break;
                case 'underline':
                    formattedText = `<u>${selectedText}</u>`;
                    break;
                case 'link':
                    formattedText = `<a href="#">${selectedText}</a>`;
                    break;
                case 'paragraph':
                    formattedText = `<p>${selectedText}</p>`;
                    break;
                case 'list':
                    formattedText = `<ul><li>${selectedText}</li></ul>`;
                    break;
            }
            
            const newValue = value.substring(0, start) + formattedText + value.substring(end);
            onChange(newValue);
        }
    };

    return (
        <div className={`border rounded-lg transition-colors ${isFocused ? 'border-blue-500 ring-1 ring-blue-500' : 'border-input'}`}>
            {/* Toolbar */}
            <div className="flex items-center justify-between p-2 bg-muted border-b border-border overflow-x-auto scrollbar-thin">
                <div className="flex items-center space-x-1 min-w-0 flex-1">
                    <button
                        type="button"
                        onClick={() => formatText('bold')}
                        className="px-2 py-1 text-xs bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded hover:bg-muted font-bold flex-shrink-0"
                        title="Bold"
                    >
                        B
                    </button>
                    <button
                        type="button"
                        onClick={() => formatText('italic')}
                        className="px-2 py-1 text-xs bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded hover:bg-muted italic flex-shrink-0"
                        title="Italic"
                    >
                        I
                    </button>
                    <button
                        type="button"
                        onClick={() => formatText('underline')}
                        className="px-2 py-1 text-xs bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded hover:bg-muted underline flex-shrink-0"
                        title="Underline"
                    >
                        U
                    </button>
                    <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1 flex-shrink-0"></div>
                    <button
                        type="button"
                        onClick={() => formatText('link')}
                        className="px-2 py-1 text-xs bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded hover:bg-muted flex-shrink-0"
                        title="Link"
                    >
                        🔗
                    </button>
                    <button
                        type="button"
                        onClick={() => formatText('paragraph')}
                        className="px-2 py-1 text-xs bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded hover:bg-muted flex-shrink-0"
                        title="Paragraph"
                    >
                        ¶
                    </button>
                    <button
                        type="button"
                        onClick={() => formatText('list')}
                        className="px-2 py-1 text-xs bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded hover:bg-muted flex-shrink-0"
                        title="List"
                    >
                        • List
                    </button>
                    <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1 flex-shrink-0"></div>
                    <button
                        type="button"
                        onClick={() => setShowVariables(!showVariables)}
                        className={`px-2 py-1 text-xs border rounded flex items-center space-x-1 flex-shrink-0 ${
                            showVariables ? 'bg-blue-100 dark:bg-blue-900/40 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300' : 'bg-card border-input hover:bg-muted'
                        }`}
                        title="Insert Variables"
                    >
                        <TagIcon className="h-3 w-3" />
                        <span className="hidden sm:inline">Variables</span>
                    </button>
                </div>
                <div className="flex items-center space-x-2 text-xs text-muted-foreground flex-shrink-0 ml-2">
                    <span>{value?.length || 0} chars</span>
                    <span>•</span>
                    <span>{value?.split('\n').length || 1} lines</span>
                </div>
            </div>

            {/* Variable Panel */}
            {showVariables && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-blue-900 dark:text-blue-100">Click to insert variables:</span>
                        <button
                            type="button"
                            onClick={() => setShowVariables(false)}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                        >
                            <XCircleIcon className="h-4 w-4" />
                        </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {availableVariables.map((variable, index) => (
                            <button
                                key={index}
                                type="button"
                                onClick={() => insertVariable(variable.name)}
                                className="px-2 py-1 bg-card border border-blue-300 dark:border-blue-700 rounded text-xs hover:bg-blue-100 dark:hover:bg-blue-900/40 hover:border-blue-400 dark:hover:border-blue-600 transition-colors text-left"
                                title={variable.description}
                            >
                                <div className="font-mono text-blue-600 dark:text-blue-400">{variable.name}</div>
                                <div className="text-muted-foreground text-xs truncate">{variable.description}</div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Text Editor */}
            <textarea
                id="rich-text-editor"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                placeholder={placeholder}
                className="w-full p-3 resize-none focus:outline-none font-mono text-sm bg-background text-foreground placeholder:text-muted-foreground"
                style={{ height: `${height}px` }}
            />
        </div>
    );
};

// Variable Management Component
const VariableManager = ({ variables, onVariablesChange }) => {
    const [newVariable, setNewVariable] = useState({ name: '', description: '' });
    const [showAddForm, setShowAddForm] = useState(false);

    const addVariable = () => {
        if (newVariable.name && newVariable.description) {
            onVariablesChange([...variables, newVariable]);
            setNewVariable({ name: '', description: '' });
            setShowAddForm(false);
        }
    };

    const removeVariable = (index) => {
        onVariablesChange(variables.filter((_, i) => i !== index));
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-foreground">Template Variables</h4>
                <button
                    type="button"
                    onClick={() => setShowAddForm(!showAddForm)}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                >
                    <PlusIcon className="h-4 w-4" />
                    <span>Add Variable</span>
                </button>
            </div>

            {showAddForm && (
                <div className="p-3 bg-muted rounded-lg border border-border">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-foreground mb-1">
                                Variable Name
                            </label>
                            <input
                                type="text"
                                value={newVariable.name}
                                onChange={(e) => setNewVariable(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="e.g., client.name"
                                className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-foreground mb-1">
                                Description
                            </label>
                            <input
                                type="text"
                                value={newVariable.description}
                                onChange={(e) => setNewVariable(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="e.g., Client's full name"
                                className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                            />
                        </div>
                    </div>
                    <div className="flex items-center justify-end space-x-2 mt-3">
                        <button
                            type="button"
                            onClick={() => setShowAddForm(false)}
                            className="px-3 py-1 text-sm text-muted-foreground hover:text-foreground"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={addVariable}
                            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            Add
                        </button>
                    </div>
                </div>
            )}

            <div className="space-y-2">
                {variables.map((variable, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-card border border-border rounded">
                        <div className="flex-1">
                            <div className="font-mono text-sm text-blue-600">{variable.name}</div>
                            <div className="text-xs text-muted-foreground">{variable.description}</div>
                        </div>
                        <button
                            type="button"
                            onClick={() => removeVariable(index)}
                            className="text-red-600 hover:text-red-800"
                        >
                            <TrashIcon className="h-4 w-4" />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Email Preview Component
const EmailPreview = ({ template, previewData = {} }) => {
    const [activeTab, setActiveTab] = useState('html');
    
    const replaceVariables = (content) => {
        if (!content) return '';
        return content.replace(/\{\{([^}]+)\}\}/g, (match, variable) => {
            return previewData[variable] || `{{${variable}}}`;
        });
    };

    const htmlContent = replaceVariables(template.htmlBody);
    const textContent = replaceVariables(template.textBody);

    return (
        <div className="space-y-4">
            <div className="flex border-b border-border">
                <button
                    type="button"
                    onClick={() => setActiveTab('html')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 ${
                        activeTab === 'html' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                >
                    HTML Preview
                </button>
                <button
                    type="button"
                    onClick={() => setActiveTab('text')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 ${
                        activeTab === 'text' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                >
                    Text Preview
                </button>
            </div>

            <div className="border border-border rounded-lg">
                <div className="p-3 bg-muted border-b border-border">
                    <div className="text-sm font-medium text-foreground">Subject: {replaceVariables(template.subject)}</div>
                </div>
                <div className="p-4">
                    {activeTab === 'html' ? (
                        <div
                            className="prose max-w-none"
                            dangerouslySetInnerHTML={{ __html: htmlContent }}
                        />
                    ) : (
                        <pre className="whitespace-pre-wrap text-sm text-foreground">{textContent}</pre>
                    )}
                </div>
            </div>
        </div>
    );
};

const EmailTemplateBuilder = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { hasFeature } = useSubscription();
    const { openDialog } = useDialog();
    const isEditing = !!id;
    
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('html'); // 'html', 'text', 'preview'
    const [aiGenerating, setAiGenerating] = useState(false);
    const [aiSuccessMessage, setAiSuccessMessage] = useState(null);
    
    const [formData, setFormData] = useState({
        name: '',
        subject: '',
        htmlBody: '',
        textBody: '',
        category: 'general',
        tags: [],
        variables: [],
        variableDefaults: {},
        omitEmptyVariables: false,
        isActive: true,
        description: '',
        code: ''
    });

    const [previewData, setPreviewData] = useState({
        'client.name': 'John Doe',
        'client.email': 'john@example.com',
        'client.company': 'Acme Corp',
        'account.name': 'Tech Solutions Inc',
        'partner.name': 'Maria Garcia',
        'current.date': new Date().toLocaleDateString(),
        'current.time': new Date().toLocaleTimeString(),
        'task.title': 'NIE Application',
        'task.dueDate': '2024-02-01',
        'agency.name': 'LiveLoveSpain',
        'agency.motto': 'Your Spanish Journey Starts Here'
    });

    const categories = [
        { value: 'general', label: 'General', color: 'bg-muted text-foreground' },
        { value: 'welcome', label: 'Welcome', color: 'bg-green-100 text-green-800' },
        { value: 'follow_up', label: 'Follow-up', color: 'bg-blue-100 text-blue-800' },
        { value: 'reminder', label: 'Reminder', color: 'bg-yellow-100 text-yellow-800' },
        { value: 'notification', label: 'Notification', color: 'bg-purple-100 text-purple-800' },
        { value: 'marketing', label: 'Marketing', color: 'bg-pink-100 text-pink-800' },
        { value: 'support', label: 'Support', color: 'bg-red-100 text-red-800' }
    ];

    useEffect(() => {
        if (isEditing) {
            loadTemplate();
        }
    }, [id, isEditing]);

    const loadTemplate = async () => {
        try {
            setLoading(true);
            const response = await emailTemplateAPI.getById(id);
            if (response.data && response.data.data) {
                const template = response.data.data;
                setFormData({
                    name: template.name || '',
                    subject: template.subject || '',
                    htmlBody: template.bodyHtml || template.htmlBody || '',
                    textBody: template.bodyText || template.textBody || '',
                    category: template.category || 'general',
                    tags: template.tags || [],
                    variables: template.variables || [],
                    variableDefaults: template.variableDefaults || {},
                    omitEmptyVariables: template.omitEmptyVariables || false,
                    isActive: template.isActive !== undefined ? template.isActive : true,
                    description: template.description || '',
                    code: template.code || template.templateId || ''
                });
            }
        } catch (error) {
            console.error('Failed to load template:', error);
            toast.error('Failed to load email template');
            navigate('/email-templates');
        } finally {
            setLoading(false);
        }
    };

    const generateTextFromHtml = (html) => {
        if (!html) return '';
        const tmp = document.createElement('DIV');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };

    const handleFormChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const addTag = (tag) => {
        if (tag && !formData.tags.includes(tag)) {
            handleFormChange('tags', [...formData.tags, tag]);
        }
    };

    const removeTag = (tag) => {
        handleFormChange('tags', formData.tags.filter(t => t !== tag));
    };

    const handleAIGenerate = async () => {
        if (!hasFeature('aiCopilot')) {
            toast.error('AI Copilot is only available on Professional and Enterprise plans');
            return;
        }

        openDialog({
            variant: 'info',
            title: 'AI Email Template Generator',
            description: 'This feature will generate email content using AI. Please enter a description of what you want.',
            confirmText: 'Continue',
            cancelText: 'Cancel',
            onConfirm: async () => {
                try {
                    setAiGenerating(true);
                    const response = await crmApi.post('/ai-copilot/email-template-generation', {
                        prompt: 'Generate email template content',
                        description: formData.description || '',
                        tone: 'professional',
                        category: formData.category,
                        subject: formData.subject || undefined,
                        variables: formData.variables.map(v => v.name || v)
                    });
                    const data = response.data;
                    if (data.success && data.data) {
                        if (data.data.htmlBody) {
                            handleFormChange('htmlBody', data.data.htmlBody);
                        }
                        if (data.data.subject && !formData.subject) {
                            handleFormChange('subject', data.data.subject);
                        }
                        if (data.data.textBody) {
                            handleFormChange('textBody', data.data.textBody);
                        }
                        
                        setAiSuccessMessage('Email template generated successfully!');
                        toast.success('Email template generated successfully!');
                        
                        setTimeout(() => {
                            setAiSuccessMessage(null);
                        }, 3000);
                    } else {
                        throw new Error(data.message || 'Failed to generate template');
                    }
                } catch (error) {
                    console.error('AI generation error:', error);
                    toast.error(error.message || 'Failed to generate email template');
                } finally {
                    setAiGenerating(false);
                }
            }
        });
    };

    const handleSave = async () => {
        if (!formData.name || !formData.subject || !formData.htmlBody || !formData.code) {
            toast.error('Please fill in all required fields (Name, Code, Subject, and HTML Body)');
            return;
        }

        try {
            setSaving(true);
            
            const variableDefaults = formData.variableDefaults && typeof formData.variableDefaults === 'object'
                ? { ...formData.variableDefaults }
                : {};
            
            const templateData = {
                name: formData.name,
                subject: formData.subject,
                bodyHtml: formData.htmlBody,
                bodyText: formData.textBody || generateTextFromHtml(formData.htmlBody),
                category: formData.category,
                tags: formData.tags,
                isActive: formData.isActive,
                code: formData.code,
                variableDefaults: variableDefaults,
                omitEmptyVariables: formData.omitEmptyVariables || false,
                description: formData.description || ''
            };
            
            if (isEditing) {
                await emailTemplateAPI.update(id, templateData);
                toast.success('Template updated successfully!');
            } else {
                await emailTemplateAPI.create(templateData);
                toast.success('Template created successfully!');
            }
            
            navigate('/email-templates');
        } catch (error) {
            console.error('Failed to save template:', error);
            const errorMessage = error.response?.data?.message || 'Failed to save template';
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <ArrowPathIcon className="h-8 w-8 animate-spin mx-auto text-blue-600 mb-4" />
                    <p className="text-muted-foreground">Loading template...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-6">
                    <Link
                        to="/email-templates"
                        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"
                    >
                        <ArrowLeftIcon className="h-4 w-4 mr-2" />
                        Back to Email Templates
                    </Link>
                    <h1 className="text-3xl font-bold text-foreground">
                        {isEditing ? 'Edit Email Template' : 'Create New Email Template'}
                    </h1>
                    <p className="text-muted-foreground mt-2">
                        {isEditing ? 'Update your email template' : 'Create a new email template with rich text editing and variables'}
                    </p>
                </div>

                {/* Form */}
                <div className="bg-card border border-border rounded-lg p-6 space-y-6">
                    {/* Basic Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Template Name *
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => handleFormChange('name', e.target.value)}
                                className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Template Code *
                            </label>
                            <input
                                type="text"
                                value={formData.code}
                                onChange={(e) => handleFormChange('code', e.target.value)}
                                className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                                placeholder="e.g., welcome_email"
                                required
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Category
                            </label>
                            <select
                                value={formData.category}
                                onChange={(e) => handleFormChange('category', e.target.value)}
                                className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                            >
                                {categories.map(cat => (
                                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Subject Line *
                            </label>
                            <input
                                type="text"
                                value={formData.subject}
                                onChange={(e) => handleFormChange('subject', e.target.value)}
                                className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                            Description
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => handleFormChange('description', e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                            placeholder="Brief description of this template's purpose..."
                        />
                    </div>

                    {/* Content Editor Tabs */}
                    <div>
                        <div className="flex border-b border-border mb-4">
                            <button
                                type="button"
                                onClick={() => setActiveTab('html')}
                                className={`px-4 py-2 text-sm font-medium border-b-2 ${
                                    activeTab === 'html'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-muted-foreground hover:text-foreground'
                                }`}
                            >
                                <DocumentTextIcon className="h-4 w-4 inline mr-2" />
                                HTML Content
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveTab('text')}
                                className={`px-4 py-2 text-sm font-medium border-b-2 ${
                                    activeTab === 'text'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-muted-foreground hover:text-foreground'
                                }`}
                            >
                                <CodeBracketIcon className="h-4 w-4 inline mr-2" />
                                Text Content
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveTab('preview')}
                                className={`px-4 py-2 text-sm font-medium border-b-2 ${
                                    activeTab === 'preview'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-muted-foreground hover:text-foreground'
                                }`}
                            >
                                <EyeIcon className="h-4 w-4 inline mr-2" />
                                Preview
                            </button>
                        </div>

                        {activeTab === 'html' && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <label className="block text-sm font-medium text-foreground">
                                        HTML Email Body *
                                    </label>
                                    <div className="flex items-center gap-2">
                                        {hasFeature('aiCopilot') && (
                                            <button
                                                type="button"
                                                onClick={handleAIGenerate}
                                                disabled={aiGenerating}
                                                className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                                title="Generate email template with AI (Professional/Enterprise only)"
                                            >
                                                {aiGenerating ? (
                                                    <>
                                                        <ArrowPathIcon className="h-4 w-4 animate-spin" />
                                                        Generating...
                                                    </>
                                                ) : (
                                                    <>
                                                        <SparklesIcon className="h-4 w-4" />
                                                        AI Generate
                                                    </>
                                                )}
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const textContent = generateTextFromHtml(formData.htmlBody);
                                                handleFormChange('textBody', textContent);
                                            }}
                                            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                        >
                                            <DocumentTextIcon className="h-4 w-4" />
                                            Generate Text Version
                                        </button>
                                    </div>
                                </div>
                                
                                {aiSuccessMessage && (
                                    <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg transition-all duration-300">
                                        <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                                        <p className="text-sm text-green-800 dark:text-green-200 font-medium">{aiSuccessMessage}</p>
                                    </div>
                                )}
                                
                                <RichTextEditor
                                    value={formData.htmlBody}
                                    onChange={(value) => handleFormChange('htmlBody', value)}
                                    height={400}
                                    placeholder="Enter your HTML email content..."
                                />
                            </div>
                        )}

                        {activeTab === 'text' && (
                            <div className="space-y-4">
                                <label className="block text-sm font-medium text-foreground">
                                    Text Email Body
                                </label>
                                
                                <RichTextEditor
                                    value={formData.textBody}
                                    onChange={(value) => handleFormChange('textBody', value)}
                                    height={300}
                                    placeholder="Enter your plain text email content..."
                                />
                            </div>
                        )}

                        {activeTab === 'preview' && (
                            <EmailPreview template={formData} previewData={previewData} />
                        )}
                    </div>

                    {/* Variables */}
                    <VariableManager
                        variables={formData.variables}
                        onVariablesChange={(variables) => handleFormChange('variables', variables)}
                    />

                    {/* Variable Defaults */}
                    {formData.variables && formData.variables.length > 0 && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <label className="block text-sm font-medium text-foreground">
                                    Variable Default Values
                                </label>
                                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <input
                                        type="checkbox"
                                        checked={formData.omitEmptyVariables}
                                        onChange={(e) => handleFormChange('omitEmptyVariables', e.target.checked)}
                                        className="rounded border-input"
                                    />
                                    <span>Omit empty variables (if no default)</span>
                                </label>
                            </div>
                            <div className="space-y-2 p-4 bg-muted rounded-lg border border-border">
                                <p className="text-xs text-muted-foreground mb-3">
                                    Set default values for variables that may be empty. If a variable is empty and has no default, it will be omitted from the email (if enabled above).
                                </p>
                                {formData.variables.map((varItem) => {
                                    const varName = typeof varItem === 'string' ? varItem : varItem.name;
                                    return (
                                        <div key={varName} className="flex items-center gap-2">
                                            <label className="text-sm font-mono text-foreground w-48 flex-shrink-0">
                                                {`{{${varName}}}`}
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.variableDefaults[varName] || ''}
                                                onChange={(e) => {
                                                    const newDefaults = {
                                                        ...formData.variableDefaults,
                                                        [varName]: e.target.value
                                                    };
                                                    if (!e.target.value.trim()) {
                                                        delete newDefaults[varName];
                                                    }
                                                    handleFormChange('variableDefaults', newDefaults);
                                                }}
                                                placeholder="Default value (optional)"
                                                className="flex-1 px-3 py-2 border border-input bg-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring text-sm"
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Tags */}
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                            Tags
                        </label>
                        <div className="flex flex-wrap gap-2 mb-2">
                            {formData.tags.map((tag, index) => (
                                <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                                    {tag}
                                    <button
                                        type="button"
                                        onClick={() => removeTag(tag)}
                                        className="text-blue-600 hover:text-blue-800"
                                    >
                                        <XCircleIcon className="h-4 w-4" />
                                    </button>
                                </span>
                            ))}
                        </div>
                        <input
                            type="text"
                            placeholder="Add a tag and press Enter"
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    addTag(e.target.value.trim());
                                    e.target.value = '';
                                }
                            }}
                            className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                        />
                    </div>

                    {/* Status */}
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="isActive"
                            checked={formData.isActive}
                            onChange={(e) => handleFormChange('isActive', e.target.checked)}
                            className="rounded border-input"
                        />
                        <label htmlFor="isActive" className="text-sm font-medium text-foreground">
                            Template is active
                        </label>
                    </div>

                    {/* Form Actions */}
                    <div className="flex items-center justify-end space-x-3 pt-6 border-t border-border">
                        <Link
                            to="/email-templates"
                            className="px-4 py-2 text-foreground bg-muted rounded-lg hover:bg-muted/80 transition-colors"
                        >
                            Cancel
                        </Link>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {saving ? (
                                <>
                                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    {isEditing ? 'Update Template' : 'Create Template'}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmailTemplateBuilder;

